import { useEffect, useState } from 'react';
import { useNotify, usePermissions, useRecordContext, useRefresh, type RaRecord } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axiosNest from '../../api/axiosNest';
import { SectionCard } from '../../shared/Panel';
import { shortDateTime } from '../../shared/dateTime';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { useFeature } from '../featureFlags/FeatureGate';
import { recordScoped } from '../../shared/recordScoped';
import { SUBCONTRACTOR_RULE_LABELS } from '../publications/publicationsApi';

/**
 * Lista reguł podwykonawców z pliku i historia reguły kontrahenta (makieta
 * back office v2, fala B, 21.10.2026; Odpowiedzi CS 20.10.2026, pkt 6:
 * „klienci bez podwykonawców są, listę przygotuje CS”). Za przełącznikiem
 * `publicationExceptionRequests`; wgrywa sekcja administracyjna. API:
 * `contractors/subcontractor-rule-import.ts` — bez zgadywania kontrahenta,
 * nieznani i niejednoznaczni idą do raportu.
 */

export const FEATURE_RULE_IMPORT = 'publicationExceptionRequests';
/** Role z sekcją administracyjną — jak przy ręcznej zmianie reguły. */
const EDITOR_ROLES = ['ADMIN', 'COMPANY_ADMIN'];
export const MIN_IMPORT_REASON_LENGTH = 10;

const ruleLabel = (rule: string | null) => SUBCONTRACTOR_RULE_LABELS[rule ?? 'nieustalona'] ?? String(rule);

export interface RuleImportReport {
  fileName: string;
  dryRun: boolean;
  applied: boolean;
  columns: { tms: string | null; nip: string | null; rule: string; notes: string | null };
  changes: Array<{
    line: number;
    contractorId: number;
    legalName: string;
    matchedBy: 'tms' | 'nip';
    from: string | null;
    to: string;
    notes: string | null;
  }>;
  unchanged: Array<{ line: number; contractorId: number; legalName: string; rule: string }>;
  unknown: Array<{ line: number; kind: 'tms' | 'nip'; value: string }>;
  ambiguous: Array<{ line: number; value: string; candidates: number }>;
  problems: Array<{ line: number; problem: string; value: string | null }>;
}

export async function uploadRuleFile(file: File, options: { dryRun: boolean; reason?: string }): Promise<RuleImportReport> {
  const form = new FormData();
  form.append('file', file);
  if (options.reason) form.append('reason', options.reason);
  const { data } = await axiosNest.post('/contractors/subcontracting-import', form, {
    params: options.dryRun ? { dryRun: 'true' } : undefined,
  });
  return data;
}

/** Raport wgrania — liczby i wiersze, których dotyczy. */
export const RuleImportReportView = ({ report }: { report: RuleImportReport }) => (
  <Stack spacing={1} data-testid="rule-import-report">
    <Typography variant="body2">
      Plik „{report.fileName}”: zmiany {report.changes.length}, bez zmian {report.unchanged.length}, nieznani
      kontrahenci {report.unknown.length}, niejednoznaczne {report.ambiguous.length}, błędne wiersze{' '}
      {report.problems.length}.
      {report.applied ? ' Zmiany zapisane.' : report.dryRun ? ' To podgląd — nic nie zapisano.' : ''}
    </Typography>
    {report.changes.length > 0 && (
      <Table size="small" data-testid="rule-import-changes">
        <TableHead>
          <TableRow>
            <TableCell>Wiersz</TableCell>
            <TableCell>Kontrahent</TableCell>
            <TableCell>Było</TableCell>
            <TableCell>Będzie</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {report.changes.map((c) => (
            <TableRow key={c.line}>
              <TableCell>{c.line}</TableCell>
              <TableCell>
                <a href={`#/contractors/${c.contractorId}/show`}>{c.legalName}</a>
                <Typography variant="caption" color="text.secondary" component="div">
                  po {c.matchedBy === 'tms' ? 'numerze z TMS' : 'NIP-ie'}
                </Typography>
              </TableCell>
              <TableCell>{ruleLabel(c.from)}</TableCell>
              <TableCell>{ruleLabel(c.to)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
    {report.unknown.length > 0 && (
      <Alert severity="warning" data-testid="rule-import-unknown">
        Nieznani kontrahenci (nie ma ich w kartotece z TMS — nie zakładamy ich ani nie zgadujemy):{' '}
        {report.unknown.map((u) => `wiersz ${u.line}: ${u.kind === 'tms' ? 'numer' : 'NIP'} ${u.value}`).join('; ')}
      </Alert>
    )}
    {report.ambiguous.length > 0 && (
      <Alert severity="warning">
        Niejednoznaczne (ten sam NIP u kilku kontrahentów — podaj numer klienta w TMS):{' '}
        {report.ambiguous.map((a) => `wiersz ${a.line}: NIP ${a.value} (${a.candidates})`).join('; ')}
      </Alert>
    )}
    {report.problems.length > 0 && (
      <Alert severity="error">
        Błędne wiersze (pominięte):{' '}
        {report.problems.map((p) => `wiersz ${p.line}: ${p.problem}${p.value ? ` („${p.value}”)` : ''}`).join('; ')}
      </Alert>
    )}
  </Stack>
);

/**
 * Przycisk i okno „Wgraj listę reguł podwykonawców” na liście kontrahentów:
 * plik → „Sprawdź plik” (podgląd, nic nie zapisuje) → powód → „Zapisz zmiany”.
 */
export const SubcontractorRuleImportButton = () => {
  const on = useFeature(FEATURE_RULE_IMPORT);
  const { permissions } = usePermissions<string>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<RuleImportReport | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (!on || !EDITOR_ROLES.includes(permissions ?? '')) return null;

  const close = () => {
    setOpen(false);
    setFile(null);
    setReport(null);
    setReason('');
  };

  const run = async (dryRun: boolean) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await uploadRuleFile(file, { dryRun, reason: dryRun ? undefined : reason.trim() });
      setReport(r);
      if (!dryRun) {
        notify(`Zapisano reguły: ${r.changes.length} kontrahentów.`, { type: 'success' });
        refresh();
      }
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="small" variant="outlined" data-testid="rule-import-open" onClick={() => setOpen(true)}>
        Wgraj listę reguł podwykonawców
      </Button>
      <Dialog open={open} onClose={() => !busy && close()} fullWidth maxWidth="md">
        <DialogTitle>Lista reguł podwykonawców z pliku</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Plik CSV albo Excel (pierwszy arkusz). Pierwszy wiersz to nagłówki: numer klienta w TMS (np. „id TMS”)
            albo NIP, reguła („dozwolony”, „warunkowy” albo „zakaz”) i opcjonalnie uwagi. Najpierw podgląd — zmiany
            zapisują się dopiero po „Zapisz zmiany”, z powodem. Kontrahentów spoza kartoteki nie zakładamy; pusta
            reguła nie czyści reguły (to robi się na karcie kontrahenta).
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
            <Button component="label" size="small" variant="outlined">
              Wybierz plik
              <input
                hidden
                type="file"
                accept=".csv,.txt,.xlsx,.xlsm"
                data-testid="rule-import-file"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setReport(null);
                }}
              />
            </Button>
            <Typography variant="body2">{file ? file.name : 'nie wybrano pliku'}</Typography>
            <Button
              size="small"
              data-testid="rule-import-check"
              disabled={!file || busy}
              onClick={() => void run(true)}
            >
              Sprawdź plik
            </Button>
          </Stack>
          {report && <RuleImportReportView report={report} />}
          {report && report.dryRun && report.changes.length > 0 && (
            <TextField
              sx={{ mt: 1.5 }}
              fullWidth
              size="small"
              label={`Powód (np. skąd lista i z jakiego dnia; co najmniej ${MIN_IMPORT_REASON_LENGTH} znaków)`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              slotProps={{ htmlInput: { 'data-testid': 'rule-import-reason' } }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Zamknij
          </Button>
          <Button
            variant="contained"
            data-testid="rule-import-apply"
            disabled={
              busy ||
              !report ||
              !report.dryRun ||
              report.changes.length === 0 ||
              reason.trim().length < MIN_IMPORT_REASON_LENGTH
            }
            onClick={() => void run(false)}
          >
            Zapisz zmiany
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface RuleHistoryRow {
  id: number;
  oldRuleLabel: string;
  newRuleLabel: string;
  source: string;
  fileName: string | null;
  fileLine: number | null;
  reason: string | null;
  changedByName: string | null;
  changedAt: string;
}

/** Historia reguły podwykonawcy przy kontrahencie — kto, kiedy, z czego na co, skąd. */
const SubcontractorRuleHistoryPanel = () => {
  const on = useFeature(FEATURE_RULE_IMPORT);
  const r = useRecordContext<RaRecord>();
  const [rows, setRows] = useState<RuleHistoryRow[] | null>(null);
  const id = r?.id;
  const ruleKey = `${String(r?.subcontractorRule ?? '')}|${String(r?.subcontractorSetAt ?? '')}`;
  useEffect(() => {
    if (!on || id === undefined) return;
    let active = true;
    axiosNest
      .get<RuleHistoryRow[]>(`/contractors/${id}/subcontracting-history`)
      .then(({ data }) => {
        if (active) setRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setRows([]);
      });
    return () => {
      active = false;
    };
  }, [on, id, ruleKey]);
  if (!on || !r) return null;
  return (
    <SectionCard title="Historia reguły podwykonawcy">
      {rows === null ? null : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Reguły jeszcze nie zmieniano.
        </Typography>
      ) : (
        <Box data-testid="rule-history">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Kiedy</TableCell>
                <TableCell>Kto</TableCell>
                <TableCell>Zmiana</TableCell>
                <TableCell>Skąd</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((h) => (
                <TableRow key={h.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{shortDateTime(h.changedAt)}</TableCell>
                  <TableCell>{h.changedByName ?? '—'}</TableCell>
                  <TableCell>
                    {h.oldRuleLabel} → {h.newRuleLabel}
                  </TableCell>
                  <TableCell>
                    {h.source === 'file'
                      ? `plik „${h.fileName ?? '—'}”${h.fileLine ? `, wiersz ${h.fileLine}` : ''}`
                      : 'karta kontrahenta'}
                    {h.reason ? (
                      <Typography variant="caption" color="text.secondary" component="div">
                        {h.reason}
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </SectionCard>
  );
};

export const SubcontractorRuleHistory = recordScoped(SubcontractorRuleHistoryPanel);
